<template>
	<template v-if="visible">
		<template v-if="insideWidgets">
			<div class="fd-tabs fd-tabs-widgets" :style="style" :class="clazz">
				<nav class="tabs tabs-form pos-wide">
					<div class="tabs-primary">
						<span v-for="(item, index) in tabs.items" :class="tabClass(item, index)"
							@click="switchToTab(item, index)"><span v-if="tabHasErrors(item)" class="fd-dot"></span><sym-icon
							v-if="hasIcon(item)" :name="item.icon"></sym-icon>{{ tabLabel(item) }}</span>
					</div>
				</nav>
				<div class="tab-content tab-content-widgets">
					<component v-for="bodyItem in currentBodies" :is="componentOfPart(bodyItem)" :key="selected" :part="partRef(bodyItem)" :data="localData" :readonly="ro" :viewmode="vm"></component>
				</div>
			</div>
		</template>
		<div v-else class="fd-tabs" :style="style" :class="clazz">
			<nav class="tabs tabs-form">
				<div class="tabs-primary">
				<span v-for="(item, index) in tabs.items" :class="tabClass(item, index)"
					  @click="switchToTab(item, index)"><span v-if="tabHasErrors(item)" class="fd-dot"></span><sym-icon
					v-if="hasIcon(item)" :name="item.icon"></sym-icon>{{ tabLabel(item) }}</span>
				</div>
			</nav>
			<div class="tab-content">
				<component v-for="bodyItem in currentBodies" :is="componentOfPart(bodyItem)" :key="selected" :part="partRef(bodyItem)" :data="localData"
						   :readonly="ro" :viewmode="vm"></component>
			</div>
		</div>
	</template>
</template>

<script setup lang="ts">
import useContainer, {type ContainerProps, containerProps} from "./Container";
import {computed, ref} from "vue";
import SymIcon from "../helpers/SymIcon.vue";
import {errorsFor} from "../Part";
import {injectErrors} from "../PartInjectKeys";
import type {Tabs} from "@t/fd/layout/Tabs";
import type {Empty} from "@t/fd/fixed/Empty";
import type {Tab} from "@t/fd/layout/Tab";

const uprops = defineProps({...containerProps});
const props = uprops as ContainerProps;

const tabs = computed<Tabs>(() => props.part as Tabs);
const {visible, componentOfPart, partRef, localData, ro, vm, path, evalExpression, style, clazz} = useContainer(props);
const selected = ref(evaluateDefaultSelection());

const currentBodies = computed(() => {
	if (selected.value < 0 || selected.value >= tabs.value.items.length) return <Empty>{type: "empty"};
	let tabPart = tabs.value.items[selected.value];
	return tabPart.items;
});

const errorMap = injectErrors();

function evaluateDefaultSelection(): number {
	if (typeof tabs.value.selected === "number") return tabs.value.selected;
	else if (typeof tabs.value.selected === "string") {
		// Determined by an expression
		return evalExpression("selected") as number;
	}
	return -1;
}

function tabHasErrors(t: Tab): boolean {
	return errorsFor(errorMap, t, path).length > 0;
}

function hasIcon(child: Tab): boolean {
	return !!child.icon;
}

function tabLabel(child: Tab): string {
	const hasLabel = typeof child.label === "string" && child.label.trim().length > 0;
	const l = hasLabel ? child.label : "";
	return (hasIcon(child) && hasLabel ? " " : "") + l;
}

function iconName(child: Tab): string | undefined {
	return child.icon;
}

function labelFor(child: Tab) {
	if (child.label) return " " + child.label;
	return "";
}

function tabClass(child: Tab, idx: number): string[] {
	let cls = ["tab"];
	if (idx === selected.value) cls.push("tab-active");
	else {
		if (idx > 0 && idx !== (selected.value + 1)) cls.push("fd-tab-separated");
	}
	return cls;
}

function switchToTab(child: Tab, idx: number) {
	selected.value = idx;
}

</script>


<style>
.fd-tabs {
	width: 100%;
	margin: 0;
	user-select: none;
	-webkit-user-select: none;
}

/** Navigation container **/

.fd-dot {
	width: 6px;
	height: 6px;
	position: absolute;
	right: -6px;
	top: 3px;
	background-color: red;
	border-radius: 3px;
}

.tab-content:empty {
	display: none;
}
.tab-content:not(:empty) {
	border: 1px solid var(--border-color);
	padding-block: var(--form-v-start-padding);
	padding-left: calc((var(--form-left-gutter) - 1px) / 2);
	padding-right: calc((var(--form-right-gutter) - 1px) / 2);
	margin: 0 calc(var(--form-right-gutter) / -2 - 1px) 0 calc(var(--form-left-gutter) / -2 - 1px);
	border-radius: var(--form-v-start-padding);
	transition: border-color 500ms;
}

.fd-tabs.fd-tabs-widgets {
	display: grid;
	grid-column: 1 / -1;
	grid-template-columns: subgrid;
}

/** Tab content under widgets */
.tab-content.tab-content-widgets {
	display: grid;
	grid-column: 1 / -1;
	grid-template-columns: subgrid;
	align-items: baseline;
	row-gap: calc(2 * var(--form-v-padding));
	column-gap: calc(2 * var(--form-h-padding));
}
</style>
